import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import type { SystemStatus } from '@hutdrop/shared';
import { api } from '../lib/api';
import { useStaffAuth } from '../lib/auth';

const since = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/**
 * Shown on every page when the background worker has stopped expiring orders. Expired holds still can't be
 * picked up (the API checks the clock itself), but stock stays reserved and customers aren't reminded.
 */
export function JobHealthBanner() {
  const { can } = useStaffAuth();
  const status = useQuery({
    queryKey: ['system-status'],
    queryFn: () => api.request<SystemStatus>('/v1/admin/system/status', { auth: true }),
    enabled: can('orders.view'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
  const sweeper = status.data?.expirySweeper;
  if (!sweeper || sweeper.status === 'ok') return null;
  return (
    <div className="job-banner" role="alert">
      <AlertTriangle size={18} aria-hidden="true" />
      <div>
        <strong>Pre-orders aren’t being expired automatically.</strong>{' '}
        {sweeper.status === 'never'
          ? 'The background worker has never run. Start it (npm run dev:worker) or check the deployment.'
          : `The background worker last succeeded ${since.format(new Date(sweeper.lastSuccessAt!))}.`}{' '}
        Expired orders still can’t be picked up, but their stock stays reserved and customers aren’t reminded.
        {sweeper.lastError ? <span className="job-banner__error"> Last error: {sweeper.lastError}</span> : null}
      </div>
    </div>
  );
}
